import { useRef, type ChangeEvent } from 'react';
import { Camera, Trash2 } from 'lucide-react';
import { ApiRequestError } from '../../../lib/api.js';
import { Button } from '../../../shell/Button.js';
import { PersonAvatar } from '../../../shell/PersonAvatar.js';
import { toast } from '../../../shell/Toast.js';
import { AVATAR_ACCEPT, validateAvatarFile } from '../avatar.js';
import { useDeleteAvatar, useUploadAvatar } from '../api.js';

interface AvatarUploadControlProps {
  name: string;
  src: string | null;
  variant: 'profile' | 'settings';
  tone?: 'default' | 'inverse';
}

export function AvatarUploadControl(props: AvatarUploadControlProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadAvatar();
  const remove = useDeleteAvatar();
  const busy = upload.isPending || remove.isPending;

  const handleFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validation = validateAvatarFile(file);
    if (validation) {
      toast('error', validation);
      event.target.value = '';
      return;
    }

    const body = new FormData();
    body.append('file', file);
    try {
      await upload.mutateAsync(body);
      toast('success', 'Profile photo updated.');
    } catch (error) {
      toast(
        'error',
        error instanceof ApiRequestError
          ? error.body.message
          : 'Failed to upload avatar.',
      );
    } finally {
      event.target.value = '';
    }
  };

  const handleRemove = async () => {
    try {
      await remove.mutateAsync();
      toast('success', 'Profile photo removed.');
    } catch (error) {
      toast(
        'error',
        error instanceof ApiRequestError
          ? error.body.message
          : 'Failed to remove avatar.',
      );
    }
  };

  const input = (
    <input
      ref={inputRef}
      type="file"
      accept={AVATAR_ACCEPT}
      onChange={handleFile}
      className="hidden"
      disabled={busy}
    />
  );

  if (props.variant === 'profile') {
    return (
      <div className="relative inline-flex">
        <button
          type="button"
          className="group relative rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-950"
          aria-label="Change profile photo"
          title="Change profile photo"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
        >
          <PersonAvatar
            name={props.name}
            src={props.src}
            size="lg"
            tone={props.tone}
          />
          <span className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-sky-600 text-white shadow-sm transition-transform group-hover:scale-105 dark:border-slate-950 dark:bg-sky-500">
            <Camera className="h-3.5 w-3.5" aria-hidden />
          </span>
        </button>
        {input}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-4">
      <PersonAvatar
        name={props.name}
        src={props.src}
        size="xl"
        className="border-2 border-slate-200 dark:border-slate-700"
      />
      <div className="flex flex-col items-start gap-2">
        {input}
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => inputRef.current?.click()}
          loading={upload.isPending}
          disabled={busy}
        >
          <Camera className="h-4 w-4" /> Change photo
        </Button>
        {props.src && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleRemove}
            loading={remove.isPending}
            disabled={busy}
          >
            <Trash2 className="h-4 w-4" /> Remove photo
          </Button>
        )}
        <p className="text-xs text-slate-500 dark:text-slate-400">
          JPEG, PNG, WebP, or GIF. Maximum 5 MB.
        </p>
      </div>
    </div>
  );
}
